import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["onClick", "title", "aria-label"]
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = { class: "tab-label" }
const _hoisted_4 = {
  key: 0,
  class: "tab-highlight"
}

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { usePermissions } from '@/composables/usePermissions';
import MoreActionsDropdown from './MoreActionsDropdown.vue';

interface Tab {
  id: string;
  name?: string;
  label: string;
  permission?: string | string[];
  permissions?: string[];  // Alternative property name for clarity
  title?: string;
  icon?: string;
  priority?: 'primary' | 'secondary';
  category?: string;
  badge?: string | number;
  [key: string]: any;
}

interface Props {
  tabs: Tab[];
  activeTab?: string;
  projectId: string | number;
  maxPrimaryTabs?: number;
  enableGrouping?: boolean;
}

interface Emits {
  select: [tabId: string];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'QuickActionsTabs',
  props: {
    tabs: {},
    activeTab: { default: '' },
    projectId: {},
    maxPrimaryTabs: { default: 5 },
    enableGrouping: { type: Boolean, default: false }
  },
  emits: ["select"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;
const emit = __emit;
const tabsContainer = ref(null);

// Initialize permissions composable
const { hasPermissionForProject, hasAnyPermissionForProject } = usePermissions();

// Filter tabs based on permissions
const permittedTabs = computed(() => {
  return props.tabs.filter((tab: Tab) => {
    // Get permissions from either 'permission', 'permissions', or both
    const tabPermissions: string[] = [];
    
    if (tab.permission) {
      if (Array.isArray(tab.permission)) {
        tabPermissions.push(...tab.permission);
      } else {
        tabPermissions.push(tab.permission);
      }
    }
    
    if (tab.permissions) {
      tabPermissions.push(...tab.permissions);
    }
    
    // If no permissions are specified, the tab is always visible
    if (tabPermissions.length === 0) return true;
    
    // If only one permission, use single permission check for better performance
    if (tabPermissions.length === 1) {
      const hasPermission = hasPermissionForProject(tabPermissions[0] as any, props.projectId);
      return hasPermission.value;
    }
    
    // If multiple permissions, check if user has any of them
    const hasAnyPermission = hasAnyPermissionForProject(tabPermissions as any[], props.projectId);
    return hasAnyPermission.value;
  });
});

// Split tabs into primary and secondary based on total count and priority
const primaryTabs = computed(() => {
  const totalTabs = permittedTabs.value.length;

  // If total tabs is less than or equal to maxPrimaryTabs, show all as primary (no dropdown needed)
  if (totalTabs <= props.maxPrimaryTabs) {
    return permittedTabs.value;
  }
  
  // Check if tabs have explicit priority set
  const explicitPrimary = permittedTabs.value.filter((tab: Tab) => tab.priority === 'primary');
  if (explicitPrimary.length > 0) {
    // Respect maxPrimaryTabs limit even with explicit priority
    return explicitPrimary.slice(0, props.maxPrimaryTabs);
  }
  
  // Otherwise, take the first N tabs as primary
  return permittedTabs.value.slice(0, props.maxPrimaryTabs);
});

const secondaryTabs = computed(() => {
  const totalTabs = permittedTabs.value.length;
  
  // If total tabs is less than or equal to maxPrimaryTabs, no secondary tabs needed
  if (totalTabs <= props.maxPrimaryTabs) {
    return [];
  }
  
  // Check if tabs have explicit priority set
  const explicitPrimary = permittedTabs.value.filter((tab: Tab) => tab.priority === 'primary');
  const explicitSecondary = permittedTabs.value.filter((tab: Tab) => tab.priority === 'secondary');
  
  if (explicitPrimary.length > 0 || explicitSecondary.length > 0) {
    // Combine: overflow primary tabs + explicit secondary tabs
    const overflowPrimary = explicitPrimary.slice(props.maxPrimaryTabs);
    return [...overflowPrimary, ...explicitSecondary];
  }
  
  // Otherwise, remaining tabs after maxPrimaryTabs are secondary
  return permittedTabs.value.slice(props.maxPrimaryTabs);
});

// Visible tabs (for rendering)
const visiblePrimaryTabs = computed(() => primaryTabs.value);
const visibleSecondaryTabs = computed(() => secondaryTabs.value);

// Debounce utility for resize events
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

// Check if tabs are overflowing and add class if needed
const checkOverflow = () => {
  if (!tabsContainer.value) return;
  
  const container = tabsContainer.value;
  const tabs = container.querySelectorAll('.nav-tab');
  
  // Check for horizontal overflow
  const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
  
  // Check if tabs are too narrow (indicating text truncation)
  let hasTruncation = false;
  let hasTextOverflow = false;
  
  if (tabs.length > 0) {
    const containerWidth = container.clientWidth - (container.offsetWidth - container.clientWidth); // Account for padding
    const availableWidthPerTab = containerWidth / tabs.length;
    
    // If each tab has less than 100px, labels likely get truncated
    hasTruncation = availableWidthPerTab < 100;
    
    // Check if any label text is being truncated
    tabs.forEach((tab: Element) => {
      const label = tab.querySelector('.tab-label');
      if (label) {
        const labelWidth = label.scrollWidth;
        const tabWidth = tab.clientWidth;
        if (labelWidth > tabWidth - 30) { // 40px for icon and padding
          hasTextOverflow = true;
        }
      }
    });
  }
  
  // Check if tabs are wrapping to multiple lines
  let hasWrapping = false;
  if (tabs.length > 1) {
    const firstTabTop = tabs[0].getBoundingClientRect().top;
    const lastTabTop = tabs[tabs.length - 1].getBoundingClientRect().top;
    hasWrapping = Math.abs(lastTabTop - firstTabTop) > 5; // 5px tolerance
  }
  
  const shouldShowIconOnly = hasHorizontalOverflow || hasTruncation || hasWrapping || hasTextOverflow;
  
  if (shouldShowIconOnly) {
    container.classList.add('has-overflow');
    container.classList.add('icon-only');
  } else {
    container.classList.remove('has-overflow');
    container.classList.remove('icon-only');
  }
};

// Scroll active tab into view
const scrollActiveTabIntoView = async () => {
  if (!tabsContainer.value || !props.activeTab) return;
  
  await nextTick();
  const activeTabElement = tabsContainer.value.querySelector('.nav-tab.active');
  if (activeTabElement) {
    // Scroll the tab into view with smooth behavior on small screens
    if (window.innerWidth <= 480) {
      const containerWidth = tabsContainer.value.offsetWidth;
      const tabWidth = activeTabElement.offsetWidth;
      const tabLeft = activeTabElement.offsetLeft;
      //const scrollLeft = tabsContainer.value.scrollLeft;
      
      // Calculate target scroll position to center the tab
      const targetScroll = tabLeft - (containerWidth - tabWidth) / 2;
      
      tabsContainer.value.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  }
};

// Select tab and ensure it's visible
const selectTab = (tabName: string) => {
  emit('select', tabName);
  // Wait for UI to update before scrolling
  nextTick(() => {
    scrollActiveTabIntoView();
  });
};

// Watch for changes in active tab or tab list
watch(() => props.activeTab, () => {
  nextTick(() => {
    setTimeout(checkOverflow, 10);
    scrollActiveTabIntoView();
  });
});
watch(() => props.tabs?.length, () => {
  nextTick(() => {
    setTimeout(checkOverflow, 10);
    scrollActiveTabIntoView();
  });
});

onMounted(() => {
  nextTick(() => {
    setTimeout(checkOverflow, 10);
    scrollActiveTabIntoView();
  });
  
  // Debounced resize handler for better performance
  const debouncedResize = debounce(() => {
    setTimeout(checkOverflow, 10);
    scrollActiveTabIntoView();
  }, 150);
  
  window.addEventListener('resize', debouncedResize);
  
  // Clean up event listener on unmount
  onUnmounted(() => {
    window.removeEventListener('resize', debouncedResize);
  });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createElementVNode("div", {
      class: "nav-tabs",
      ref_key: "tabsContainer",
      ref: tabsContainer
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePrimaryTabs.value, (tab) => {
        return (_openBlock(), _createElementBlock("button", {
          key: tab.name || tab.id,
          class: _normalizeClass(['nav-tab', { active: _ctx.activeTab === (tab.name || tab.id) }]),
          onClick: ($event: any) => (selectTab(tab.name || tab.id)),
          title: tab.title || tab.label,
          "aria-label": tab.title || tab.label
        }, [
          _createElementVNode("span", {
            class: "tab-icon",
            innerHTML: tab.icon
          }, null, 8, _hoisted_2),
          _createElementVNode("span", _hoisted_3, _toDisplayString(tab.label), 1),
          (_ctx.activeTab === (tab.name || tab.id))
            ? (_openBlock(), _createElementBlock("span", _hoisted_4))
            : _createCommentVNode("", true)
        ], 10, _hoisted_1))
      }), 128)),
      (visibleSecondaryTabs.value.length > 0)
        ? (_openBlock(), _createBlock(MoreActionsDropdown, {
            key: 0,
            tabs: visibleSecondaryTabs.value,
            activeTab: _ctx.activeTab,
            projectId: _ctx.projectId,
            enableGrouping: _ctx.enableGrouping,
            onSelect: selectTab
          }, null, 8, ["tabs", "activeTab", "projectId", "enableGrouping"]))
        : _createCommentVNode("", true)
    ], 512)
  ]))
}
}

})